import {useContext, useState} from 'react'

import PropTypes from 'prop-types'

import {htmlStringToReactElement} from '@s-ui/js/lib/react'
import AtomButton, {atomButtonDesigns, atomButtonShapes, atomButtonSizes} from '@s-ui/react-atom-button'
import Context from '@s-ui/react-context'
import FormBuilder, {
  checkConstraintsFactory,
  formBuilderChangeFieldById,
  formBuilderPickFieldById
} from '@s-ui/react-form-builder'
import {useRouter} from '@s-ui/react-router'
import FormConditionsAndPrivacyCheckbox from '@adv-mt/mt-form-conditions-and-privacy-checkbox'
import FormConsents from '@adv-mt/mt-form-consents-financial-services'

import {BASE_CLASS_NAME} from '../../config.js'
import {useFormConfiguration} from '../../hooks/useFormConfiguration.js'
import {usePublishMyAdWithPlateAnalyticsTrack} from '../../hooks/usePublishMyAdWithPlateAnalyticsTrack.js'
import Spinner from '../Spinner/index.js'
import Title from '../Title/index.js'

const NO_OP = () => {}

const getClearedNumericValue = ({value}) => value?.replace(/\D/g, '')
const isValidForm = errors => !Object.values(errors).some(error => error.length > 0)

const FormSellerPlate = ({title, onSubmit = NO_OP}) => {
  const {domain, i18n} = useContext(Context)
  const router = useRouter()

  const {formConfiguration, formFieldIds, initFields, SUIComponentsConfig} = useFormConfiguration()
  const {errorTypes, trackErrorPlate, trackFormFieldSelected, trackReceiveVehicleData} =
    usePublishMyAdWithPlateAnalyticsTrack()

  const [fields, setFields] = useState(initFields)
  const [isLoading, setIsLoading] = useState(false)
  const [validatedErrors, setValidatedErrors] = useState({})
  const [isPrivacyChecked, setIsPrivacyChecked] = useState(false)
  const [isPrivacyError, setIsPrivacyError] = useState(false)
  const [isSubmitError, setIsSubmitError] = useState(false)

  const config = domain.get('config')
  const {PUBLISH_MY_AD_PATH_PATTERN} = config.get('page')

  const setErrors = errors =>
    setValidatedErrors(prevErrors => {
      const allErrors = {
        ...prevErrors,
        ...errors
      }

      return allErrors
    })

  const checkConstraints = ({fieldId} = {}) => {
    const checkParams = fieldId ? {for: fieldId} : {all: true}
    const _errors = checkConstraintsFactory(formConfiguration)(checkParams)
    setErrors(_errors)
    return _errors
  }

  const handleOnBlur = fieldId => {
    const {value} = formBuilderPickFieldById(formConfiguration?.form.fields, fieldId)

    if (value) {
      trackFormFieldSelected({fieldId})
    }

    checkConstraints({fieldId})
  }

  const handleOnChange = ({__FIELD_CHANGED__, __FORM_STATE__, ...newFields}) => {
    let formattedValue

    if (__FIELD_CHANGED__ === formFieldIds.kilometersId) {
      const clearedValue = getClearedNumericValue({value: newFields[__FIELD_CHANGED__]})
      formattedValue = clearedValue ? i18n.n(Number(clearedValue)) : ''
    } else if (__FIELD_CHANGED__ === formFieldIds.plate) {
      const upperCasedPlate = newFields[__FIELD_CHANGED__].toUpperCase()
      formattedValue = upperCasedPlate.replace(/[^A-Z0-9]/g, '')
    }

    newFields[__FIELD_CHANGED__] = formattedValue
    formBuilderChangeFieldById(formConfiguration.form.fields, __FIELD_CHANGED__, {
      value: formattedValue
    })

    setFields({...newFields})
  }

  const handleOnSkip = async () => {
    if (fields.km) {
      const [draftError, draftResponse] = await domain.get('obtain_draft_ad_publisher_use_case').execute()

      if (!draftError) {
        const {
          data: {id: adId}
        } = draftResponse

        const _fields = {
          bodyTypeDoors: '',
          carPlate: '',
          fuelId: '',
          km: getClearedNumericValue({value: fields.km}),
          makeId: '',
          modelId: '',
          plateRegistrationDate: '',
          publishInMA: true,
          transmissionTypeId: '',
          versionId: '',
          year: ''
        }

        await domain.get('edit_draft_ad_publisher_use_case').execute({
          fields: _fields,
          adId
        })
      }
    }

    router.push(`${PUBLISH_MY_AD_PATH_PATTERN}?withPlate=true&withPlateStatus=plateSkip`)
  }

  const handleOnSubmit = async () => {
    setIsLoading(true)

    const errors = checkConstraints()

    if (!isPrivacyChecked) {
      setIsPrivacyError(true)
    }

    if (isValidForm(errors) && isPrivacyChecked) {
      const plate = fields[formFieldIds.plate]

      domain
        .get('get_vehicles_info_by_plate_use_case')
        .execute({plate})
        .then(async ([error, response]) => {
          if (error) {
            trackErrorPlate({errorType: errorTypes.vehicleNotFound})

            setErrors({
              [formFieldIds.plate]: [i18n.t('PUBLISH_AD.WITH_PLATE.NOT_FOUND_FIELD_ERROR_TEXT')]
            })

            formBuilderChangeFieldById(formConfiguration.form.fields, formFieldIds.notification, {
              hidden: false
            })

            setIsSubmitError(true)
            setIsLoading(false)

            return
          }
          trackReceiveVehicleData()

          const km = getClearedNumericValue({value: fields[formFieldIds.kilometersId]})
          onSubmit({km, plate, vehicles: [...response.items]})
        })
    } else {
      trackErrorPlate({
        errorType: errorTypes.fieldError,
        fieldErrors: errors,
        isPrivacyChecked
      })
      setIsLoading(false)
    }
  }

  return (
    <>
      {isLoading && <Spinner />}
      <Title
        description={htmlStringToReactElement(
          i18n.t('PUBLISH_AD.WITH_PLATE.NOT_VISIBLE_NOT_ACCESS', {
            highlightStart: '<strong>',
            highlightEnd: '</strong>'
          })
        )}
        title={title}
      />
      <div className={`${BASE_CLASS_NAME}-form`}>
        <p className={`${BASE_CLASS_NAME}-formRequiredFieldsText`}>{i18n.t('FORM.ALL_FIELDS_ARE_REQUIRED')}</p>

        <FormBuilder
          errors={validatedErrors}
          initFields={initFields}
          json={formConfiguration}
          onBlur={handleOnBlur}
          onChange={handleOnChange}
          setErrors={setValidatedErrors}
        >
          {SUIComponentsConfig({handleOnSkip, isSubmitError})}
        </FormBuilder>

        <FormConditionsAndPrivacyCheckbox
          checked={isPrivacyChecked}
          isError={isPrivacyError}
          onChange={value => {
            setIsPrivacyChecked(value)
            setIsPrivacyError(false)
          }}
        />
        <FormConsents />
      </div>
      <div className={`${BASE_CLASS_NAME}-buttonGroup`}>
        <div className={`${BASE_CLASS_NAME}-button`}>
          <AtomButton
            disabled={isLoading}
            fullWidth
            onClick={handleOnSubmit}
            shape={atomButtonShapes.CIRCULAR}
            size={atomButtonSizes.LARGE}
          >
            <span className={`${BASE_CLASS_NAME}-buttonText`}>{i18n.t('PUBLISH_AD.WITH_PLATE.FIND_VEHICLE')}</span>
          </AtomButton>
        </div>
        <div className={`${BASE_CLASS_NAME}-buttonSecondary`}>
          <AtomButton
            design={atomButtonDesigns.LINK}
            disabled={isLoading}
            fullWidth
            onClick={handleOnSkip}
            shape={atomButtonShapes.CIRCULAR}
            size={atomButtonSizes.LARGE}
          >
            <span className={`${BASE_CLASS_NAME}-buttonText`}>{i18n.t('PUBLISH_AD.WITH_PLATE.SKIP')}</span>
          </AtomButton>
        </div>
      </div>
    </>
  )
}

FormSellerPlate.displayName = 'FormSellerPlate'

FormSellerPlate.propTypes = {
  onSubmit: PropTypes.func,
  title: PropTypes.string.isRequired
}

export default FormSellerPlate
